import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import Breadcrum from '../../components/breadcrum'
import { toast } from 'react-hot-toast'
import {
  createCustomise,
  getCustomiseById,
  updateCustomise,
} from '../../api/customiseApi'

const AddCustomise = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditMode = Boolean(id)

  useEffect(() => {
    document.title = `${isEditMode ? 'Edit ' : 'Add '}Customise`
  }, [isEditMode])

  // Initialize form with default values
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      title: '',
      userEmail: '',
      price: '',
      company: '',
    },
  })

  // Fetch customise news data if in edit mode
  const { data: customiseData, isLoading: isLoadingcustomise } = useQuery({
    queryKey: ['customise', id],
    queryFn: () => getCustomiseById(id),
    enabled: isEditMode,
    onSuccess: (data) => {
      console.log('Query success handler triggered with data:', data)
      populateForm(data)
    },
    onError: (error) => {
      toast.error('Failed to load customise news data')
      console.error('Error loading customise news:', error)
      navigate('/dealdashboard/customsie') // Redirect back to customise list on error
    },
  })

  // Separate function to handle form population to ensure it runs
  const populateForm = (data) => {
    if (!data) {
      console.error('No data available to populate form')
      return
    }

    console.log('Populating form with data:', data)

    // Use timeout to ensure this runs after render cycle
    setTimeout(() => {
      // Explicitly set each form field
      setValue('title', data.title || '')
      setValue('userEmail', data.userEmail || '')
      setValue('price', data.price || '')
      setValue('company', data.company || '')
    }, 0)
  }

  // Ensure form is populated when data changes
  useEffect(() => {
    if (isEditMode && customiseData) {
      console.log('customiseData updated in useEffect:', customiseData)
      populateForm(customiseData)
    }
  }, [customiseData, isEditMode])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createCustomise,
    onSuccess: () => {
      toast.success('Customise added successfully!')
      reset()
      navigate('/dealdashboard/customise') // Redirect to Customise list
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(
        error.response?.data?.message || 'Failed to create Customise report'
      )
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => updateCustomise(id, data),
    onSuccess: () => {
      toast.success('Customise updated successfully!')
      navigate('/dealdashboard/customise') // Redirect to Customise list
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(
        error.response?.data?.message || 'Failed to update Customise report'
      )
    },
  })

  const onSubmit = (data) => {
    console.log('Form submitted with data:', data)

    // Submit the form data based on mode
    if (isEditMode) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  // Show loading state while fetching data in edit mode
  if (isEditMode && isLoadingcustomise) {
    return (
      <>
        <Breadcrum heading="DSJ Insight" subheading="Edit Customise" />
        <div className="max-w-8xl text-center mx-auto px-4 py-6">
          <p className="text-gray-600">Loading customise data...</p>
        </div>
      </>
    )
  }

  return (
    <>
      <Breadcrum
        heading="DSJ Insight"
        subheading={isEditMode ? 'Edit Customise' : 'Add Customise'}
      />
      <div className="max-w-8xl text-left mx-auto px-4 py-6">
        {isEditMode && customiseData && (
          <div className="bg-blue-50 p-4 mb-6 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-800">Editing Report:</h3>
            <p className="text-blue-700">{customiseData.title}</p>
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Heading Input */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-md shadow mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Title
              </h2>
              <input
                type="text"
                placeholder="Enter report title ..."
                className={`border ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                defaultValue={customiseData?.title || ''}
                {...register('title', {
                  required: 'Title is required',
                })}
              />
              {errors.title && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* User email */}
            <div className="bg-white p-6 rounded-md shadow mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                User Email
              </h2>
              <input
                type="text"
                placeholder="user email id"
                className={`border ${
                  errors.userEmail ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                defaultValue={customiseData?.userEmail || ''}
                {...register('userEmail', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address',
                  },
                })}
              />
              {errors.userEmail && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.userEmail.message}
                </p>
              )}
            </div>

            {/* company */}
            <div className="bg-white p-6 rounded-md shadow">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Company Name
              </h2>
              <input
                type="text"
                placeholder="add company name"
                className={`border ${
                  errors.company ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                defaultValue={customiseData?.company || ''}
                {...register('company', {
                  required: 'company is required',
                })}
              />
              {errors.company && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.company.message}
                </p>
              )}
            </div>

            {/* price */}
            <div className="bg-white p-6 rounded-md shadow">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Price
              </h2>
              <input
                type="number"
                placeholder="price ?..."
                className={`border ${
                  errors.price ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                defaultValue={customiseData?.price || ''}
                {...register('price', {
                  required: 'Price is required',
                  valueAsNumber: true,
                  min: {
                    value: 0,
                    message: 'Price must be a positive number',
                  },
                })}
              />
              {errors.price && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.price.message}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="bg-orange-500 hover:bg-orange-600 text-white text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all disabled:bg-orange-300 disabled:cursor-not-allowed"
            >
              {createMutation.isPending || updateMutation.isPending
                ? isEditMode
                  ? 'Updating...'
                  : 'Adding...'
                : isEditMode
                ? 'Update'
                : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dealdashboard/customise')}
              className="ml-4 bg-gray-100 hover:bg-gray-200 text-gray-800 text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default AddCustomise
